import { useFormState, useWatch } from "react-hook-form";
import PropTypes from "prop-types";

import Dropdown from "shared/components/inputs/form-fields/Dropdown";
import VStack from "shared/components/layout/stack/VStack";
import Form from "shared/components/inputs/form/Form";
import ModalActions from "shared/components/feedback/modals/ModalActions";
import ToggleInputForm from "shared/components/inputs/form-fields/ToggleInputForm";

import { sendNotification } from "shared/components/feedback/notification/notification";
import FormFeedbackNotificationText from "shared/components/feedback/error/FormFeedbackNotificationText";

import useClientRateCards from "features/admin/clients/single/useClientRateCards";
import { getDropdownOptions } from "features/admin/offices/OfficesForm";
import { useRateCardsApi } from "features/admin/projects/useProjects";

const ModalCTA = ({
    isEditing,
    isLoading,
    onClose,
    handleOnClickDeleteRateCard,
}) => {
    const { isValid } = useFormState();
    const formData = useWatch();
    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                label: isEditing ? "Save" : "Add",
                disabled: !isValid,
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            {...(isEditing && {
                tertiaryAction: {
                    type: "button",
                    leadingIcon: "delete",
                    label: "Delete",
                    onClick: () => handleOnClickDeleteRateCard(formData),
                },
            })}
            as="fieldset"
            disabled={isLoading}
        />
    );
};

function transformRateCardDataToFormData(rateCardData = {}) {
    return {
        rateCardDropdown: rateCardData.RateCardTableId,
        default: rateCardData?.UseAsDefault ?? false,
    };
}

function transformFormDataToRateCardData(formData = {}, rateCardData = {}) {
    const newRateCardData = { ...rateCardData };
    newRateCardData.RateCardTableId = formData.rateCardDropdown;
    newRateCardData.UseAsDefault = formData.default;

    return newRateCardData;
}

const ClientRateCardForm = ({ rateCardIdToEdit, onClose }) => {
    const { clientRateCards, createClientRateCard, deleteClientRateCard } =
        useClientRateCards();

    const { rateCards } = useRateCardsApi();

    if (clientRateCards.isLoading) {
        return <div>Loading...</div>;
    }

    if (clientRateCards.error) {
        return <div>Error</div>;
    }

    const isEditing = rateCardIdToEdit !== undefined;
    const modifier = createClientRateCard;

    const currentClientRateCardData = clientRateCards?.data?.find(
        (rateCard) => rateCard.RateCardTableId === rateCardIdToEdit
    );

    if (isEditing && !currentClientRateCardData) {
        return <div>Could not find rate card</div>;
    }

    const handleOnClickDeleteRateCard = async (prevData) => {
        const response = await deleteClientRateCard.mutateAsync({ clientRateCardId: rateCardIdToEdit });
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isDeleting={true}
                name={currentClientRateCardData.RateCardTableName}
            />,
            {
                close: true,
                action: true,
                actionProps: {
                    label: "Undo",
                    onClick: () => onSubmit(prevData),
                },
            }
        );
    };

    const rateCardOptions = rateCards.isLoading
        ? []
        : getDropdownOptions(
              rateCards?.data,
              "RateCardTableId",
              "RateCardTableName"
          );

    const onSubmit = async (formData) => {
        const clientRateCardsData = transformFormDataToRateCardData(
            formData,
            currentClientRateCardData
        );

        const allData = {
            clientRateCardId: clientRateCardsData.RateCardTableId,
            useAsDefault: clientRateCardsData.UseAsDefault,
        };

        const response = await modifier.mutateAsync(allData);
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isEditing={isEditing}
                name={
                    isEditing
                        ? clientRateCardsData.RateCardTableName
                        : rateCardOptions?.find(
                              (f) => f.value === formData.rateCardDropdown
                          )?.label
                }
            />
        );
    };

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={transformRateCardDataToFormData(
                currentClientRateCardData
            )}
            mode="onChange"
        >
            <VStack spacing="Three">
                <VStack spacing="Two">
                    <Dropdown
                        name="rateCardDropdown"
                        validations={{
                            required: true,
                        }}
                        label="Rate Card"
                        itemsAreLoading={rateCards.isLoading}
                        disabledOptionsList={
                            clientRateCards.isLoading
                                ? []
                                : clientRateCards.data.map((data) => ({
                                      value: data.RateCardTableId,
                                  }))
                        }
                        disabledOptionTooltipText="Already in this Rate Card"
                        options={rateCardOptions}
                    />
                    <ToggleInputForm
                        defaultSelection={
                            currentClientRateCardData?.UseAsDefault ?? false
                        }
                        name="default"
                        label={["Default", "Default"]}
                    />
                </VStack>
                <ModalCTA
                    isEditing={isEditing}
                    handleOnClickDeleteRateCard={handleOnClickDeleteRateCard}
                    onClose={onClose}
                    isLoading={modifier.isLoading}
                />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    isEditing: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    handleOnClickDeleteRateCard: PropTypes.func
};

ClientRateCardForm.propTypes = {
    rateCardIdToEdit: PropTypes.number,
    onClose: PropTypes.func.isRequired
};

export default ClientRateCardForm;
